import React, { useEffect, useState } from "react";
import Typography from '@mui/material/Typography';
import ItemWithSubMenu from "./components/ItemWithSubMenu";
import ItemWithoutSubMenu from "./components/ItemWithoutSubMenu";
import ClearOutlinedIcon from '@mui/icons-material/ClearOutlined';
import ExpandCircleDownIcon from '@mui/icons-material/ExpandCircleDown';
import ItemWithSubMenuSettings from "./components/ItemWithSubMenuSettings";
import { navBarItems } from "./NavMenuItems";
import { Sidebar, Menu } from 'react-pro-sidebar';
import { EndPoints } from "../../utils/apiEndpoints";
import { useUserStore } from "../../store/useUserStore";
import { darkModeBackgroud } from "../../utils/constants";
import { useLocation, useNavigate } from 'react-router-dom';
import { useDarkModeStore } from "../../store/useDarkModeStore";
import { useExpandNavMenu } from "../../store/useExpandNavMenu";
import { useLanguageStore } from "../../store/useLanguageStore";
import { ReactComponent as Logo } from '../../images/Logo_svg.svg'
import { ReactComponent as LogoText } from '../../images/Logo_text.svg'
import { NavMenuItem, SubMenuSettings } from "../../domain/helpers/NavMenuItem";

export type ClassActiveNav = "active-nav-item-dark" | "active-nav-item-dark-settings" | "active-nav-item-settings" | "active-nav-item" | "sub-menu-expanded" | "";

interface NavMenuProps {
  isMobile?: boolean;
  handleClose?: () => void;
}

const NavMenu = ({ isMobile = false, handleClose }: NavMenuProps) => {

  const navigate = useNavigate();
  const location = useLocation().pathname;

  const { user } = useUserStore();
  const { darkMode } = useDarkModeStore();
  const { language } = useLanguageStore();
  const { isCollapsed, setIsCollapsed } = useExpandNavMenu();

  const [expandedSubMenuId, setExpandedSubMenuId] = useState<number | null>(null);
  const [expandedSubMenuSettingId, setExpandedSubMenuSettingId] = useState<number | null>(null);
  const [selectedNav, setSelectedNav] = useState<string>(location === "/" ? EndPoints.LOGIN : location);

  const isAdminRole = user?.["http://schemas.microsoft.com/ws/2008/06/identity/claims/role"] === 'Admin';

  const handleRedirect = (path: string) => {
    //handle close to close the nav menu in mobile mode
    handleClose && handleClose();
    navigate(path);
    setSelectedNav(path);
  };

  const handleSubMenuClick = (id: number) => setExpandedSubMenuId(prevId => (prevId === id ? null : id));
  const handleSubMenuSettingsClick = (id: number) => setExpandedSubMenuSettingId(prevId => (prevId === id ? null : id));

  const activeSubMenu = (i: NavMenuItem): ClassActiveNav => {
    const isExpanded = expandedSubMenuId === i.id;
    const isActive = i.subMenu?.some(item => item.route === selectedNav);
    if (isActive) return darkMode ? 'active-nav-item-dark' : 'active-nav-item';
    else if (isExpanded) return 'sub-menu-expanded'
    else return '';
  }

  const activeSubSubMenu = (i: SubMenuSettings): ClassActiveNav => {
    const isExpanded = expandedSubMenuSettingId === i.id;
    const isActive = i.subMenuItemSettings?.some(item => item.route === selectedNav);
    if (isActive) return darkMode ? 'active-nav-item-dark-settings' : 'active-nav-item-settings';
    else if (isExpanded) return 'sub-menu-expanded'
    else return '';
  }

  useEffect(() => {
    if (isMobile) {
      document.querySelectorAll('.css-1wvake5').forEach(element => {
        if (element instanceof HTMLElement) element.style.width = '100%';
      });
    }
  }, [isMobile]);

  useEffect(() => {
    const navItemElement = document.querySelector('.css-dip3t8');
    if (navItemElement) navItemElement.setAttribute('style', `background: ${darkMode ? darkModeBackgroud : 'white'} !important;`);
  }, [darkMode]);

  const getMenuItemHoverStyle = () => {
    return {
      transition: 'background-color 0.3s ease',
      backgroundColor: darkMode ? '#555555' : '#f3f3f3',
    };
  };

  const RenderLogo = () => <Logo width="34" height="34" />

  return (
    <div
      className="position-relative nav-bar-base"
      style={{ background: darkMode ? darkModeBackgroud : 'white' }}
    >
      <div className="nav-bar-sticky">
        <div
          role="button"
          className="top-nav-bar-item"
          title="Line Desk - Ultimate Ticketing System"
          style={{ background: darkMode ? darkModeBackgroud : 'white', }}
          onClick={() => navigate(EndPoints.ANALYTICS)}
        >

          {
            isCollapsed
              ? <RenderLogo />
              : <div className="d-flex flex-row gap-1 align-items-center">
                <RenderLogo />
                <LogoText width="130" height="105" />
              </div>
          }
        </div>

        {
          !isMobile
          && <div
            role="button"
            className="absolute-item p-2"
            style={{ background: darkMode ? darkModeBackgroud : '#F3F3F7', borderRadius: '50%' }}
            onClick={() => setIsCollapsed(!isCollapsed)}
          >
            <ExpandCircleDownIcon
              className="color-pink smooth-transition"
              sx={{
                width: '22px',
                height: '22px',
                borderRadius: '50%',
                transition: 'transform .6s ease',
                transform: isCollapsed ? 'rotate(270deg)' : 'rotate(90deg)'
              }}
            />
          </div>
        }
        {
          isMobile
          && <div
            role="button"
            className="absolute-item p-2"
            style={{ background: darkMode ? darkModeBackgroud : '#F3F3F7', borderRadius: '50%', right: 10 }}
            onClick={handleClose || undefined}
          >
            <ClearOutlinedIcon
              className="color-pink smooth-transition"
              sx={{ width: '22px', height: '22px', borderRadius: '50%', }}
            />
          </div>
        }

        <Sidebar
          collapsed={isCollapsed}
          className="position-relative"
        >
          <Menu
            rootStyles={{ background: darkMode ? darkModeBackgroud : 'white' }}
            menuItemStyles={{
              button: {
                [`&.active`]: {
                  color: '#b6c8d9',
                  backgroundColor: '#13395e',
                },
                [`&:hover`]: getMenuItemHoverStyle(),
              },
            }}
          >
            {
              navBarItems?.map(navItem =>
                <React.Fragment key={navItem.groupName}>
                  {
                    navItem.groupName !== 'none' &&
                    !(navItem.groupName === 'SETTINGS' && user?.["http://schemas.microsoft.com/ws/2008/06/identity/claims/role"] === 'Employee')
                    &&
                    <Typography
                      className={`nav-item-section ${isCollapsed && 'd-flex justify-content-center'} ${darkMode && 'text-white'}`}
                      sx={isCollapsed ? { margin: '0 !important', paddingRight: '3px' } : {}}
                      component="div"
                    >
                      <hr style={{ width: '20px', opacity: 1 }} />
                      {!isCollapsed && language[navItem.groupName]}
                    </Typography>
                  }
                  {
                    navItem.items
                      .filter(i => isAdminRole ? i : i.role === 'Employee')
                      .map(i =>
                        <React.Fragment key={i.id}>
                          {
                            i.subMenu
                              ? <ItemWithSubMenu
                                navMenuItem={i}
                                selectedNav={selectedNav}
                                isCollapsed={isCollapsed}
                                expandedSubMenuId={expandedSubMenuId}
                                activeSubMenu={activeSubMenu}
                                handleRedirect={handleRedirect}
                                handleSubMenuClick={handleSubMenuClick}
                                setExpandedSubMenuSettingId={setExpandedSubMenuSettingId}
                              />
                              : i.subMenuSettings
                                ? <ItemWithSubMenuSettings
                                  navMenuItem={i}
                                  isCollapsed={isCollapsed}
                                  selectedNav={selectedNav}
                                  expandedSubMenuId={expandedSubMenuId}
                                  expandedSubMenuSettingId={expandedSubMenuSettingId}
                                  activeSubMenu={activeSubMenu}
                                  handleRedirect={handleRedirect}
                                  activeSubSubMenu={activeSubSubMenu}
                                  handleSubMenuClick={handleSubMenuClick}
                                  handleSubMenuSettingsClick={handleSubMenuSettingsClick}
                                />
                                : <ItemWithoutSubMenu
                                  navMenuItem={i}
                                  isCollapsed={isCollapsed}
                                  selectedNav={selectedNav}
                                  handleRedirect={handleRedirect}
                                  setExpandedSubMenuId={setExpandedSubMenuId}
                                  setExpandedSubMenuSettingId={setExpandedSubMenuSettingId}
                                />
                          }
                        </React.Fragment>
                      )
                  }
                </React.Fragment>
              )
            }
          </Menu>
        </Sidebar >
      </div>
    </div>
  );
};

export default NavMenu;
